import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Plus,
  Minus,
  Trash2,
  Package,
  MapPin,
  Search,
  Loader2,
  Save,
  ShoppingBag
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Card, CardContent } from './ui/card';
import { Order, OrderItem, Product, Customer } from '../types';
import { ProductPicker } from './ProductPicker';
import { toast } from 'sonner';
import { updateOrder } from '../services/orderService';
import { searchProducts } from '../services/productService';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { 
  distributeDiscountAcrossItems, 
  LineItemForDiscount,
  getEffectivePrice as getEffectivePriceUtil,
  getLineItemTotal
} from '../utils/discountCalculations';
import { DiscountCode } from '../services/discountService';

// Local discount validation function (simplified version of customer app logic)
const validateDiscountCodeLocal = async (
  code: string, 
  userId: string, 
  orderSubtotal: number,
  userEmail: string
): Promise<{
  isValid: boolean;
  discount?: DiscountCode;
  discountAmount?: number;
  error?: string;
}> => {
  try {
    if (!code || !code.trim()) {
      return { isValid: false, error: 'Please enter a discount code' };
    }

    if (orderSubtotal <= 0) {
      return { isValid: false, error: 'Cart subtotal must be greater than $0' };
    }

    // Get discount configuration from Firebase
    const discountRef = doc(db, 'config', 'discounts');
    const discountDoc = await getDoc(discountRef);
    
    if (!discountDoc.exists()) {
      return { isValid: false, error: 'Invalid discount code' };
    }
    
    const data = discountDoc.data();
    const discountCodes = data.discountCodes || [];
    
    const discount = discountCodes.find((d: DiscountCode) => 
      d.code.toUpperCase() === code.trim().toUpperCase() && 
      d.status === 'active'
    );
    
    if (!discount) {
      return { isValid: false, error: 'Invalid discount code' };
    }
    
    // Check date validity
    const now = new Date();
    const startDate = discount.startDate?.toDate ? discount.startDate.toDate() : new Date(discount.startDate);
    const endDate = discount.endDate?.toDate ? discount.endDate.toDate() : new Date(discount.endDate);
    
    if (now < startDate || now > endDate) {
      return { isValid: false, error: 'Discount code has expired' };
    }
    
    // Check minimum order amount
    if (orderSubtotal < (discount.minOrderAmount || 0)) {
      return { 
        isValid: false, 
        error: `Minimum cart subtotal is $${(discount.minOrderAmount || 0).toFixed(2)}` 
      };
    }
    
    // Calculate discount amount
    let discountAmount = 0;
    switch (discount.type) {
      case 'percentage':
        discountAmount = (orderSubtotal * discount.value) / 100;
        break;
      case 'fixed':
        discountAmount = Math.min(discount.value, orderSubtotal);
        break;
      case 'free_shipping':
        discountAmount = 0;
        break;
    }
    
    discountAmount = Math.max(0, Math.round(discountAmount * 100) / 100);
    
    return {
      isValid: true,
      discount,
      discountAmount
    };
  } catch (error) {
    console.error('Error validating discount code:', error);
    return { isValid: false, error: 'Failed to validate discount code' };
  }
};

// Currency conversion helpers using order's stored metadata
const getOrderCurrency = (order: Order) => {
  return order?.purchaseCurrency || 'USD';
};

const getOrderExchangeRates = (order: Order) => {
  return order?.exchangeRateSnapshot || { USD: 1, CAD: 1.35, GBP: 0.8 };
};

// Convert USD price to order's display currency using stored exchange rate
const getConvertedItemPriceFromOrder = (usdPrice: number, order: Order) => {
  const currency = getOrderCurrency(order);
  const rates = getOrderExchangeRates(order);
  
  if (currency === 'USD') {
    return usdPrice;
  }
  
  const rate = rates[currency] || 1;
  return Math.ceil(usdPrice * rate);
};

// Format currency with order's currency symbol and code
const formatOrderCurrency = (amount: number, order: Order) => {
  const currency = getOrderCurrency(order);
  
  if (currency === 'USD') {
    return `$${amount.toFixed(2)} USD`;
  } else if (currency === 'CAD') {
    return `$${amount.toFixed(2)} CAD`;
  } else if (currency === 'GBP') {
    return `£${amount.toFixed(2)} GBP`;
  }
  
  return `$${amount.toFixed(2)}`;
};

const getCustomDutiesFromOrder = (order: Order) => {
  const duties = (order as any)?.customDuties;
  if (!duties || !duties.amountUsd) return 0;

  const currency = getOrderCurrency(order);
  const rates = getOrderExchangeRates(order);

  if (
    duties.currency &&
    duties.currency === currency &&
    typeof duties.amountConverted === 'number'
  ) {
    return duties.amountConverted;
  }

  const usdAmount = duties.amountUsd;
  if (currency === 'USD') return usdAmount;
  const rate = rates[currency as keyof typeof rates] || 1;
  return Math.ceil(usdAmount * rate);
};

// Helper function to get effective price (sale price if on sale, otherwise regular price)
const getEffectivePrice = (item: OrderItem) => {
  if (item.onSale && item.onSalePrice && item.onSalePrice < item.price) {
    return item.onSalePrice;
  }
  return item.price;
};



interface EditOrderModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onOrderUpdated: (updatedOrder: Order) => void;
}

export function EditOrderModal({ order, isOpen, onClose, onOrderUpdated }: EditOrderModalProps) {
  const [editedOrder, setEditedOrder] = useState<Order | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: ''
  });
  const [priorityProcessing, setPriorityProcessing] = useState(false);
  const [reappliedDiscount, setReappliedDiscount] = useState<{ amount: number; type: string } | null>(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [summaryOverrides, setSummaryOverrides] = useState<{ subtotal?: number; shipping?: number; priorityFee?: number; total?: number }>({});

  // Initialize modal state when order changes
  useEffect(() => {
    if (order && isOpen) {
      setEditedOrder({ ...order });
      setItems([...order.items]);
      
      // Initialize shipping address
      if (order.customer?.address) {
        setShippingAddress({
          name: order.customer.name || '',
          line1: order.customer.address.line1 || '',
          line2: order.customer.address.line2 || '',
          city: order.customer.address.city || '',
          state: order.customer.address.state || '',
          postal_code: order.customer.address.postal_code || '',
          country: order.customer.address.country || '',
          phone: order.customer.phone || ''
        });
      }
      
      // Initialize priority processing
      setPriorityProcessing(order.shipping?.priorityProcessing || false);
      // Seed summary overrides from persisted order values so manual edits stick on reopen
      const safeNumber = (val: any) => (typeof val === 'number' && Number.isFinite(val) ? val : undefined);
      setSummaryOverrides({
        subtotal: safeNumber(order.subtotal),
        shipping: safeNumber(order.shipping?.cost),
        priorityFee: safeNumber(order.shipping?.priorityFee),
        total: safeNumber(order.total)
      });
      
      // Reset search
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [order, isOpen]);

  // Reapply discount function (currency-aware with line-item distribution)
  const reapplyDiscount = useCallback(async () => {
    // Prevent infinite loops during discount application
    if (applyingDiscount) {
      return;
    }
    
    if (!editedOrder?.discount || !items.length) {
      setReappliedDiscount(null);
      // Clear line-item discounts when no discount applied
      setItems(currentItems => currentItems.map(item => ({
        ...item,
        discountAmount: undefined,
        discountPercentage: undefined,
        discountedTotal: undefined
      })));
      return;
    }
    
    setApplyingDiscount(true);

    try {
      // Calculate subtotal in USD for discount validation (use original USD prices)
      const usdSubtotal = items.reduce((total, item) => {
        const unitPriceUsd = getEffectivePrice(item);
        return total + (unitPriceUsd * item.quantity);
      }, 0);

      // Validate the existing discount code with USD subtotal
      const validation = await validateDiscountCodeLocal(
        editedOrder.discount.code,
        editedOrder.customer?.id || 'admin',
        usdSubtotal,
        editedOrder.customer?.email || 'admin@jampackd.store'
      );

      if (validation.isValid && validation.discount && validation.discountAmount !== undefined) {
        // For fixed discounts, cap discount amount at cart total to prevent overapplication
        let finalDiscountAmount = validation.discountAmount;
        const discountConfig = validation.discount as DiscountCode;
        if (discountConfig.type === 'fixed') {
          finalDiscountAmount = Math.min(discountConfig.value, usdSubtotal);
        }
        
        // For percentage and fixed discounts, calculate line-item distribution
        if (discountConfig.type === 'percentage' || discountConfig.type === 'fixed') {
          // Convert items to the format expected by discount calculations
          const itemsForDiscount: LineItemForDiscount[] = items.map(item => ({
            id: item.id,
            quantity: item.quantity,
            price: item.price,
            onSalePrice: item.onSalePrice,
            onSale: item.onSale
          }));

          // Distribute the discount across items
          const distribution = distributeDiscountAcrossItems(
            itemsForDiscount,
            discountConfig.type as 'percentage' | 'fixed',
            discountConfig.type === 'percentage' ? discountConfig.value : finalDiscountAmount
          );

          // Update items with line-item discounts
          setItems(currentItems => currentItems.map(item => {
            const itemDiscount = distribution.lineItemDiscounts.find(d => d.itemId === item.id);
            if (itemDiscount) {
              return {
                ...item,
                discountAmount: itemDiscount.discountAmount,
                discountPercentage: itemDiscount.discountPercentage,
                discountedTotal: itemDiscount.discountedTotal
              };
            }
            return {
              ...item,
              discountAmount: undefined,
              discountPercentage: undefined,
              discountedTotal: undefined
            };
          }));
        } else {
          // For free shipping discounts, clear line-item discount fields
          setItems(currentItems => currentItems.map(item => ({
            ...item,
            discountAmount: undefined,
            discountPercentage: undefined,
            discountedTotal: undefined
          })));
        }
        
        setReappliedDiscount({
          amount: Math.round(finalDiscountAmount * 100) / 100,
          type: discountConfig.type
        });
      } else {
        setReappliedDiscount(null);
        // Clear line-item discounts when validation fails
        setItems(currentItems => currentItems.map(item => ({
          ...item,
          discountAmount: undefined,
          discountPercentage: undefined,
          discountedTotal: undefined
        })));
      }
    } catch (error) {
      console.error('Error reapplying discount:', error);
      setReappliedDiscount(null);
      // Clear line-item discounts on error
      setItems(currentItems => currentItems.map(item => ({
        ...item,
        discountAmount: undefined,
        discountPercentage: undefined,
        discountedTotal: undefined
      })));
    } finally {
      setApplyingDiscount(false);
    }
  }, [editedOrder, applyingDiscount]);

  // Create a stable key for core item properties (excluding discount fields)
  // This prevents infinite loops when discount fields are updated
  const coreItemsKey = useMemo(() => {
    return items.map(item => 
      `${item.id}-${item.quantity}-${item.price}-${item.onSalePrice || 'null'}-${item.onSale || false}`
    ).join('|');
  }, [items]);

  // Reapply discount when core item properties change (if original order had a discount)
  useEffect(() => {
    reapplyDiscount();
  }, [coreItemsKey, editedOrder?.discount?.code, editedOrder?.discount?.type, editedOrder?.discount?.amount, reapplyDiscount]);

  // Open advanced product picker (matches Products page variant modal search)
  const handleSearch = async () => {
    setShowProductPicker(true);
  };

  // Add product to order
  const handleAddProduct = (product: Product, event?: React.MouseEvent) => {
    // Prevent event bubbling if event is provided
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const existingItem = items.find(item => item.id === product.id);
    
    if (existingItem) {
      // Increase quantity if already exists
      handleQuantityChange(product.id, existingItem.quantity + 1);
      toast.success(`Increased ${product.name} quantity to ${existingItem.quantity + 1}`);
    } else {
      // Add new item
      const newItem: OrderItem = {
        id: product.id,
        name: product.name,
        quantity: 1,
        price: product.price,
        onSalePrice: product.onSalePrice,
        onSale: product.onSale || false,
        image: product.image,
        total: getEffectivePrice({ 
          price: product.price, 
          onSalePrice: product.onSalePrice, 
          onSale: product.onSale 
        } as OrderItem),
        listingQuantity: product.listingQuantity
      };
      
      setItems(prevItems => [...prevItems, newItem]);
      toast.success(`Added ${product.name} to order`);
    }
    
    // Clear search
    setSearchQuery('');
    setSearchResults([]);
  };

  // Update item quantity
  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    setItems(items.map(item => 
      item.id === itemId 
        ? { 
            ...item, 
            quantity: newQuantity,
            total: getEffectivePrice(item) * newQuantity
          }
        : item
    ));
  };

  // Remove item from order
  const handleRemoveItem = (itemId: string) => {
    setItems(items.filter(item => item.id !== itemId));
    toast.success('Item removed from order');
  };

  // Update shipping address
  const handleAddressChange = (field: string, value: string) => {
    setShippingAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save order changes
  const handleSaveOrder = async () => {
    if (!editedOrder) return;

    setIsSaving(true);
    try {
      // Use calculated totals from current state
      const subtotal = displayedSummary.subtotal;
      const total = displayedSummary.total;

      // Helper function to filter undefined values from nested objects
      const filterUndefined = (obj: any): any => {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj;
        if (obj instanceof Date) return obj;
        
        return Object.fromEntries(
          Object.entries(obj).filter(([, value]) => value !== undefined)
        );
      };

      // Deeply remove undefined values so Firestore doesn't receive them
      const removeUndefinedDeep = (value: any): any => {
        if (value === undefined) return undefined;
        if (value === null) return null;
        if (value instanceof Date) return value;
        if (Array.isArray(value)) {
          return value
            .map((v) => removeUndefinedDeep(v))
            .filter((v) => v !== undefined);
        }
        if (typeof value === 'object') {
          const result: any = {};
          for (const [key, val] of Object.entries(value)) {
            if (val !== undefined) {
              const cleaned = removeUndefinedDeep(val);
              if (cleaned !== undefined) {
                result[key] = cleaned;
              }
            }
          }
          return result;
        }
        return value;
      };

      // Prepare updated order
      const updatedOrder: Order = {
        ...editedOrder,
        items,
        subtotal,
        total,
        customer: filterUndefined({
          // Preserve all existing customer properties
          ...editedOrder.customer,
          // Update specific fields from the form
          name: shippingAddress.name,
          phone: shippingAddress.phone,
          address: {
            line1: shippingAddress.line1,
            line2: shippingAddress.line2,
            city: shippingAddress.city,
            state: shippingAddress.state,
            postal_code: shippingAddress.postal_code,
            country: shippingAddress.country
          }
        }) as Customer,
        shipping: {
          ...editedOrder.shipping,
          priorityProcessing,
          cost: displayedSummary.shipping,
          priorityFee: displayedSummary.priorityFee,
          address: {
            ...editedOrder.shipping.address,
            fullName: shippingAddress.name,
            line1: shippingAddress.line1,
            line2: shippingAddress.line2,
            city: shippingAddress.city,
            state: shippingAddress.state,
            zipCode: shippingAddress.postal_code,
            country: shippingAddress.country,
            phoneNumber: shippingAddress.phone
          }
        },
        // Only include tracking if it exists and is not undefined
        ...(editedOrder.tracking && Object.keys(filterUndefined(editedOrder.tracking)).length > 0 
          ? { tracking: filterUndefined(editedOrder.tracking) } 
          : {}),
        // Only include orderTracking if it exists and is not undefined
        ...(editedOrder.orderTracking && Object.keys(filterUndefined(editedOrder.orderTracking)).length > 0 
          ? { orderTracking: filterUndefined(editedOrder.orderTracking) } 
          : {}),
        // Update discount with reapplied amount and line-item discounts if available
        ...(editedOrder.discount && reappliedDiscount ? {
          discount: {
            ...editedOrder.discount,
            amount: reappliedDiscount.amount,
            // Include line-item discounts for percentage/fixed discounts
            lineItemDiscounts: (reappliedDiscount.type === 'percentage' || reappliedDiscount.type === 'fixed') ? 
              items.reduce((acc, item) => {
                if (item.discountAmount !== undefined && item.discountPercentage !== undefined) {
                  acc[item.id] = {
                    discountAmount: item.discountAmount,
                    discountPercentage: item.discountPercentage
                  };
                }
                return acc;
              }, {} as { [itemId: string]: { discountAmount: number; discountPercentage: number; } }) : 
              undefined
          }
        } : editedOrder.discount ? { discount: editedOrder.discount } : {}),
        updatedAt: new Date()
      };

      // Update order in database with undefined values removed
      const sanitizedUpdate = removeUndefinedDeep(updatedOrder) as Partial<Order>;
      await updateOrder(updatedOrder.id, sanitizedUpdate);
      
      onOrderUpdated(updatedOrder);
      onClose();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    } finally {
      setIsSaving(false);
    }
  };

  // Real-time calculation of order totals (matching cart screen logic exactly)
  const orderCalculations = useMemo(() => {
    if (!editedOrder) return { subtotal: 0, total: 0, shipping: 0, priorityFee: 0, discount: 0 };
    
    // Create a temporary order with current state for calculations
    const tempOrder = {
      ...editedOrder,
      shipping: {
        ...editedOrder.shipping,
        priorityProcessing,
        priorityFee: priorityProcessing ? (editedOrder.shipping?.priorityFee || 3.00) : 0
      }
    };
    
    // Calculate converted subtotal using per-item conversion with line-item discounts
    const convertedSubtotal = items.reduce((total, item) => {
      // If item has line-item discount, use discounted total
      if (item.discountedTotal !== undefined) {
        const convertedDiscountedTotal = getConvertedItemPriceFromOrder(item.discountedTotal, tempOrder);
        return total + convertedDiscountedTotal;
      } else {
        // Otherwise use original calculation
        const unitPriceUsd = getEffectivePrice(item);
        const convertedUnitPrice = getConvertedItemPriceFromOrder(unitPriceUsd, tempOrder);
        return total + (convertedUnitPrice * item.quantity);
      }
    }, 0);
    
    // Calculate converted shipping (individual conversion)
    const baseShippingCost = tempOrder.shipping?.cost || 0;
    const shippingDiscountType = reappliedDiscount?.type || tempOrder.discount?.type;
    const convertedShipping = shippingDiscountType === 'free_shipping' 
      ? 0 
      : getConvertedItemPriceFromOrder(baseShippingCost, tempOrder);
    
    // Calculate converted priority fee (individual conversion)
    const convertedPriorityFee = getConvertedItemPriceFromOrder(tempOrder.shipping?.priorityFee || 0, tempOrder);
    
    // Calculate converted discount - only for free shipping (percentage/fixed discounts already applied at line-item level)
    const discountToUse = reappliedDiscount || { 
      amount: tempOrder.discount?.amount || 0, 
      type: tempOrder.discount?.type || 'fixed' 
    };
    const convertedDiscount = discountToUse.type === 'free_shipping' 
      ? 0 // Free shipping shows as $0 shipping, not as a discount line
      : 0; // Percentage/fixed discounts already applied to line items
    
    const convertedDuties = getCustomDutiesFromOrder(tempOrder);
    
    // Total calculation: subtotal + shipping + priority - discount + duties
    const convertedTotal = convertedSubtotal + convertedShipping + convertedPriorityFee - convertedDiscount + convertedDuties;
    
    return {
      subtotal: convertedSubtotal,
      total: convertedTotal,
      shipping: convertedShipping,
      priorityFee: convertedPriorityFee,
      discount: convertedDiscount,
      duties: convertedDuties
    };
  }, [items, editedOrder, priorityProcessing, reappliedDiscount]);

  const displayedSummary = useMemo(() => {
    const subtotal = summaryOverrides.subtotal ?? orderCalculations.subtotal;
    const shipping = summaryOverrides.shipping ?? orderCalculations.shipping;
    const priorityFee = summaryOverrides.priorityFee ?? orderCalculations.priorityFee;
    const duties = orderCalculations.duties || 0;
    const autoTotal = subtotal + shipping + priorityFee + duties;
    const total = summaryOverrides.total ?? autoTotal;
    return { subtotal, shipping, priorityFee, total, duties };
  }, [summaryOverrides, orderCalculations]);

  const handleSummaryOverrideChange = (field: 'subtotal' | 'shipping' | 'priorityFee' | 'total', value: string) => {
    const trimmed = value.trim();
    if (trimmed === '') {
      setSummaryOverrides(prev => ({ ...prev, [field]: undefined }));
      return;
    }
    const num = Number(trimmed);
    if (Number.isNaN(num)) return;
    setSummaryOverrides(prev => ({ ...prev, [field]: num }));
  };

  if (!order || !editedOrder) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10200] flex items-center justify-center p-4"
          style={{
            margin: 0,
            padding: "1rem",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Package className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      Edit Order
                    </h2>
                    <p className="text-white/80 text-sm">
                      Order #{order.orderNumber || order.id}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6 space-y-6">
                {/* Order Items Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <ShoppingBag className="h-5 w-5 text-purple-600" />
                      Order Items ({items.length})
                    </h3>
                    <Button onClick={handleSearch} disabled={isSearching} className="bg-purple-600 hover:bg-purple-700 text-white">
                      {isSearching ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          Browse Products
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Current Items */}
                  <div className="space-y-3">
                    {items.map((item) => (
                      <Card key={item.id} className="border border-gray-200">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-16 h-16 object-cover rounded-lg"
                            />
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{item.name}</h4>
                              <p className="text-sm text-gray-500">
                                Unit Price: {formatOrderCurrency(getConvertedItemPriceFromOrder(getEffectivePrice(item), editedOrder), editedOrder)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="w-12 text-center font-medium">{item.quantity}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="text-right w-24">
                              {item.discountedTotal !== undefined ? (
                                // Show discounted price with original price crossed out
                                <div className="space-y-1">
                                  {(() => {
                                    const originalTotal = getConvertedItemPriceFromOrder(getEffectivePrice(item), editedOrder) * item.quantity;
                                    
                                    // Calculate discount amount using proportional distribution in display currency
                                    const convertedDiscountAmount = (() => {
                                      const orderCurrency = getOrderCurrency(editedOrder);
                                      
                                      if (!editedOrder.discount || orderCurrency === 'USD') {
                                        // For USD or no discount, use direct conversion
                                        return getConvertedItemPriceFromOrder(item.discountAmount!, editedOrder);
                                      }
                                      
                                      // Get all discounted items for proportional calculation
                                      const allDiscountedItems = items.filter(orderItem => orderItem.discountAmount && orderItem.discountAmount > 0);
                                      
                                      // Calculate total USD discount across all items
                                      const totalUsdDiscount = allDiscountedItems.reduce((sum, orderItem) => sum + (orderItem.discountAmount || 0), 0);
                                      
                                      // Convert total discount to display currency
                                      const totalConvertedDiscount = getConvertedItemPriceFromOrder(totalUsdDiscount, editedOrder);
                                      
                                      // Calculate this item's proportion of total USD discount
                                      const itemDiscountProportion = (item.discountAmount!) / totalUsdDiscount;
                                      
                                      // Find this item's index for "last item gets remainder" logic
                                      const currentItemIndex = allDiscountedItems.findIndex(orderItem => orderItem.id === item.id);
                                      const isLastItem = currentItemIndex === allDiscountedItems.length - 1;
                                      
                                      if (isLastItem) {
                                        // Last item: get exact remainder to ensure total precision
                                        const otherItemsTotal = allDiscountedItems.slice(0, -1).reduce((sum, otherItem) => {
                                          const otherProportion = (otherItem.discountAmount || 0) / totalUsdDiscount;
                                          return sum + Math.round(totalConvertedDiscount * otherProportion * 100) / 100;
                                        }, 0);
                                        return totalConvertedDiscount - otherItemsTotal;
                                      } else {
                                        // All other items: calculate proportional discount and round
                                        return Math.round(totalConvertedDiscount * itemDiscountProportion * 100) / 100;
                                      }
                                    })();
                                    
                                    // Calculate discounted total mathematically: original - discount
                                    const convertedDiscountedTotal = Math.max(0, originalTotal - convertedDiscountAmount);
                                    
                                    return (
                                      <>
                                        <p className="text-sm text-gray-400 line-through">
                                          {formatOrderCurrency(originalTotal, editedOrder)}
                                        </p>
                                        <p className="font-semibold">
                                          {formatOrderCurrency(convertedDiscountedTotal, editedOrder)}
                                        </p>
                                        <p className="text-xs text-green-600">
                                          -{formatOrderCurrency(convertedDiscountAmount, editedOrder)} discount
                                        </p>
                                      </>
                                    );
                                  })()}
                                </div>
                              ) : (
                                // Show regular price
                                <p className="font-semibold">
                                  {formatOrderCurrency(getConvertedItemPriceFromOrder(getEffectivePrice(item), editedOrder) * item.quantity, editedOrder)}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Shipping Address Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-purple-600" />
                    Shipping Address
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={shippingAddress.name}
                        onChange={(e) => handleAddressChange('name', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        value={shippingAddress.phone}
                        onChange={(e) => handleAddressChange('phone', e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="line1">Address Line 1</Label>
                      <Input
                        id="line1"
                        value={shippingAddress.line1}
                        onChange={(e) => handleAddressChange('line1', e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="line2">Address Line 2 (Optional)</Label>
                      <Input
                        id="line2"
                        value={shippingAddress.line2}
                        onChange={(e) => handleAddressChange('line2', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={shippingAddress.city}
                        onChange={(e) => handleAddressChange('city', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State/Province</Label>
                      <Input
                        id="state"
                        value={shippingAddress.state}
                        onChange={(e) => handleAddressChange('state', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal_code">Postal Code</Label>
                      <Input
                        id="postal_code"
                        value={shippingAddress.postal_code}
                        onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={shippingAddress.country}
                        onChange={(e) => handleAddressChange('country', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Priority Processing */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium text-gray-800">
                        Priority Processing
                      </Label>
                      <p className="text-xs text-gray-500 mt-1">
                        Order will be prioritized and packed ahead of normal orders
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={priorityProcessing}
                      onChange={(e) => setPriorityProcessing(e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <Separator />

                {/* Order Summary */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Order Summary</h3>
                  
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="flex items-center justify-between gap-4">
                      <span>Subtotal</span>
                      <Input
                        type="number"
                        inputMode="decimal"
                        className="w-32 h-9 text-right"
                        value={summaryOverrides.subtotal ?? ''}
                        placeholder={formatOrderCurrency(orderCalculations.subtotal, editedOrder)}
                        onChange={(e) => handleSummaryOverrideChange('subtotal', e.target.value)}
                        min={0}
                      />
                    </div>
                    
                    {(editedOrder.shipping?.cost != null) && (
                      <div className="flex items-center justify-between gap-4">
                        <span>Shipping</span>
                        <Input
                          type="number"
                          inputMode="decimal"
                          className="w-32 h-9 text-right"
                          value={summaryOverrides.shipping ?? ''}
                          placeholder={formatOrderCurrency(orderCalculations.shipping, editedOrder)}
                          onChange={(e) => handleSummaryOverrideChange('shipping', e.target.value)}
                          min={0}
                        />
                      </div>
                    )}

                    {orderCalculations.duties > 0 && (
                      <div className="flex items-center justify-between gap-4">
                        <span>Customs Duties</span>
                        <div className="w-32 h-9 flex items-center justify-end text-sm font-medium text-amber-700">
                          {formatOrderCurrency(orderCalculations.duties, editedOrder)}
                        </div>
                      </div>
                    )}
                    
                    {priorityProcessing && (
                      <div className="flex items-center justify-between gap-4">
                        <span>Priority Processing</span>
                        <Input
                          type="number"
                          inputMode="decimal"
                          className="w-32 h-9 text-right"
                          value={summaryOverrides.priorityFee ?? ''}
                          placeholder={formatOrderCurrency(orderCalculations.priorityFee, editedOrder)}
                          onChange={(e) => handleSummaryOverrideChange('priorityFee', e.target.value)}
                          min={0}
                        />
                      </div>
                    )}
                    
                    {editedOrder.discount && editedOrder.discount.type === 'free_shipping' && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount ({editedOrder.discount.code})</span>
                        <span>Applied to shipping</span>
                      </div>
                    )}
                    
                    {editedOrder.discount && (editedOrder.discount.type === 'percentage' || editedOrder.discount.type === 'fixed') && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount ({editedOrder.discount.code})</span>
                        <span>Applied to individual items</span>
                      </div>
                    )}
                    
                    <Separator />
                    
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-semibold text-lg">Total</span>
                      <Input
                        type="number"
                        inputMode="decimal"
                        className="w-32 h-9 text-right text-purple-600 font-semibold"
                        value={summaryOverrides.total ?? ''}
                        placeholder={formatOrderCurrency(displayedSummary.total, editedOrder)}
                        onChange={(e) => handleSummaryOverrideChange('total', e.target.value)}
                        min={0}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Picker Modal */}
            <ProductPicker
              isOpen={showProductPicker}
              onClose={() => setShowProductPicker(false)}
              onSelect={(product) => {
                handleAddProduct(product);
                setShowProductPicker(false);
              }}
              excludeProductIds={items.map((i) => i.id)}
              context="order-editing"
            />

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 p-6 flex justify-end gap-3">
              <Button variant="outline" onClick={onClose} disabled={isSaving}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveOrder} 
                disabled={isSaving || items.length === 0}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Render the ProductPicker overlay when invoked
// (No wrapper component needed)
